import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { 
  Image, CheckCircle, XCircle, Flag, User, AlertTriangle, 
  ChevronLeft, ChevronRight, Eye
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminPhotoModeration = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [statusFilter, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/moderation/photos?status=${statusFilter}&page=${page}&limit=${limit}`),
        axios.get(`${API}/moderation/stats`)
      ]);
      setReports(reportsRes.data.reports || []);
      setTotal(reportsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch moderation data:', error);
      toast.error('Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (report, action) => {
    setSelectedReport(report);
    setActionType(action);
    setActionReason('');
    setSelectedPhotoIndex(null);
    setShowActionModal(true);
  };

  const executeAction = async () => {
    if (!selectedReport || !actionType) return;
    
    setRemoving(true);
    try {
      const params = new URLSearchParams({
        action: actionType
      });
      if (actionReason) params.append('reason', actionReason);
      if (selectedPhotoIndex !== null) params.append('remove_photo_index', selectedPhotoIndex);
      
      await axios.post(`${API}/moderation/photos/${selectedReport.id}?${params.toString()}`);
      
      toast.success(`Report ${actionType}ed successfully`);
      setShowActionModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setRemoving(false);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    return photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`;
  };

  const openPhotoPreview = (photo) => {
    setPreviewPhoto(photo);
    setShowPhotoPreview(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="photo-moderation-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Photo Moderation</h1>
        <p className="text-[#A1A1AA]">Review and moderate reported photos</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              <span className="text-[#A1A1AA]">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-500">{stats.pending_count || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-[#A1A1AA]">Resolved (24h)</span>
            </div>
            <p className="text-2xl font-bold text-green-500">{stats.resolved_24h || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <span className="text-[#A1A1AA]">Rejected</span>
            </div>
            <p className="text-2xl font-bold text-red-500">{stats.by_status?.resolved || 0}</p>
          </div>
          <div className="bg-[#111] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-2">
              <Flag className="w-5 h-5 text-orange-500" />
              <span className="text-[#A1A1AA]">Flagged</span>
            </div>
            <p className="text-2xl font-bold text-orange-500">{stats.by_status?.flagged || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={val => { setStatusFilter(val); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[#A1A1AA] text-sm">{total} reports found</span>
      </div>

      {/* Reports Grid */}
      {reports.length === 0 ? (
        <div className="text-center py-16">
          <Image className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white">No reports in queue</h3>
          <p className="text-[#A1A1AA]">All caught up! Check back later.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map(report => (
            <div key={report.id} className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
              {/* User Photos */}
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 overflow-hidden">
                    {report.reported_user?.public_photos?.[0] ? (
                      <img 
                        src={getPhotoUrl(report.reported_user.public_photos[0])} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <User className="w-6 h-6 text-[#52525B] m-2" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {report.reported_user?.display_name || report.reported_user?.username || 'Unknown'}
                    </p>
                    <p className="text-xs text-[#52525B]">@{report.reported_user?.username}</p>
                  </div>
                </div>
                
                {/* Photos Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {(report.reported_user?.public_photos || []).slice(0, 6).map((photo, idx) => (
                    <div 
                      key={idx} 
                      className="aspect-square rounded-lg bg-white/5 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity relative group"
                      onClick={() => openPhotoPreview(photo)}
                    >
                      <img 
                        src={getPhotoUrl(photo)} 
                        alt="" 
                        className="w-full h-full object-cover" 
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Report Info */}
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-xs text-[#52525B] uppercase mb-1">Report Reason</p>
                  <p className="text-white text-sm">{report.reason}</p>
                </div>
                <div className="flex items-center justify-between text-xs text-[#52525B]">
                  <span>Reported {new Date(report.created_at).toLocaleDateString()}</span>
                  <span className={`px-2 py-1 rounded ${
                    report.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                    report.status === 'flagged' ? 'bg-orange-500/20 text-orange-500' :
                    'bg-green-500/20 text-green-500'
                  }`}>
                    {report.status}
                  </span>
                </div>

                {/* Actions */}
                {report.status === 'pending' && (
                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => handleAction(report, 'approve')}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      data-testid={`approve-${report.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleAction(report, 'reject')}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      data-testid={`reject-${report.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction(report, 'flag')}
                      className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                      data-testid={`flag-${report.id}`}
                    >
                      <Flag className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="ghost"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="text-[#A1A1AA]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="text-[#A1A1AA]">
            Page {page} of {Math.ceil(total / limit)}
          </span>
          <Button
            variant="ghost"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / limit)}
            className="text-[#A1A1AA]"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      )}

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' && 'Approve Report'}
              {actionType === 'reject' && 'Remove Photo'}
              {actionType === 'flag' && 'Flag for Review'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {actionType === 'reject' && selectedReport?.reported_user?.public_photos && (
              <div>
                <p className="text-sm text-[#A1A1AA] mb-2">Select photo to remove (optional):</p>
                <div className="grid grid-cols-4 gap-2">
                  {selectedReport.reported_user.public_photos.map((photo, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setSelectedPhotoIndex(idx === selectedPhotoIndex ? null : idx)}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                        selectedPhotoIndex === idx ? 'border-red-500' : 'border-transparent'
                      }`}
                    >
                      <img src={getPhotoUrl(photo)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-[#A1A1AA] mb-2">
                {actionType === 'approve' ? 'Notes (optional):' : 'Reason:'}
              </p>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={actionType === 'reject' ? 'Violated community guidelines...' : 'Add notes...'}
                className="bg-white/5 border-white/10 min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowActionModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={executeAction}
              disabled={removing}
              className={
                actionType === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                'bg-orange-600 hover:bg-orange-700'
              }
            >
              {removing ? 'Processing...' : `Confirm ${actionType}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      <Dialog open={showPhotoPreview} onOpenChange={setShowPhotoPreview}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl">
          {previewPhoto && (
            <img 
              src={getPhotoUrl(previewPhoto)} 
              alt="Preview" 
              className="w-full h-auto rounded-lg" 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPhotoModeration;
